import {
  FormProvider,
  FormScope,
  useControlField,
  useForm,
} from '@rvf/react-router'
import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { useRouteLoaderData } from 'react-router'
import { Spinner } from '~/components/common'
import { FieldContainer } from '~/components/fields'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
} from '~/components/ui'
import { useDebounce } from '~/hooks/misc'
import { Client, reportPdfValidator, type Listing } from '~/models'
import PdfFormContent from './form-content'
import { PdfClientBrowser } from './pages'
import PdfClientDownload from './pdf-client-download'
import ReportPdfSwitch from './report-pdf-switch'

type ListingApiResponse = {
  count: number
  listings: Listing[]
  nextOffset: number
  success: boolean
  error?: string
}

const getListings = async (
  find: {},
  limit: number,
  offset: number
): Promise<ListingApiResponse> => {
  const response = await fetch('/api/listings', {
    method: 'POST',
    body: JSON.stringify({ find, limit, offset }),
  })
  const result = (await response.json()) as ListingApiResponse
  if (!result.success) {
    console.error('Error loading listings', result.error)
    throw new Error(`There was a problem fetching results: ${result.error}`)
  }
  return result
}

function PdfTitle({
  scope,
  initialTitle,
}: {
  scope: FormScope<string>
  initialTitle: string
}) {
  const [titleValue, setTitleValue] = useState<string>(initialTitle)
  const debouncedTitleValue = useDebounce(titleValue, 1000)
  const [_, setTitle] = useControlField<string>('title', scope)

  useEffect(() => {
    setTitle(debouncedTitleValue)
  }, [debouncedTitleValue, setTitle])

  return (
    <FieldContainer label="Title" id="title">
      <Input
        id="title"
        name="title"
        value={titleValue}
        onChange={(e) => {
          setTitleValue(e.target.value)
        }}
      />
    </FieldContainer>
  )
}

export default function CreateDialog({
  children,
  listingIds,
  singleListing = false,
}: {
  children: React.ReactNode
  listingIds: string[]
  singleListing?: boolean
}) {
  const { client } = useRouteLoaderData('root') as unknown as { client: Client }
  const [isOpen, setIsOpen] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    setIsLoaded(true)
  }, [])

  const listingsQuery = useQuery({
    queryKey: ['create-pdf-listings', 'disable-cache', ...listingIds],
    queryFn: () => {
      return getListings({ id: { $in: listingIds } }, 50, 0)
    },
    refetchOnWindowFocus: false,
    enabled: isOpen,
  })

  const listings = useMemo(() => {
    return listingsQuery.data?.listings || []
  }, [listingsQuery])

  const sortedListings = useMemo(() => {
    return (
      (listingIds
        .filter((id) => listings.find((listing) => listing.id === id))
        .map((id) => {
          return listings.find((listing) => listing.id === id)
        })
        .filter((v) => v) as Listing[]) || []
    )
  }, [listingIds, listings])

  const defaultValues = useMemo(() => {
    return {
      title: '',
      options: {
        type: 'flyers',
        showTitlePage: false,
        showEcDevContact: true,
        showContents: false,
        showBrokerProfile: true,
        showMapPage: false,
        showBrokers: true,
        showImages: false,
        preparedFor: '',
        notes: '',
      },
    }
  }, [])

  const form = useForm({
    submitSource: 'state',
    defaultValues: defaultValues,
    validator: reportPdfValidator,
  })
  // ok here is the plan emmit the scope
  const rootScope = form.scope()
  const optionScope = form.scope('options')

  const [title] = useControlField<string>('title', rootScope)
  const [type] = useControlField<string>('type', optionScope)
  const [preparedFor] = useControlField<string>('preparedFor', optionScope)
  const [notes] = useControlField<string>('notes', optionScope)
  const [showTitlePage] = useControlField<boolean>('showTitlePage', optionScope)
  const [showEcDevContact] = useControlField<boolean>(
    'showEcDevContact',
    optionScope
  )
  const [showContents] = useControlField<boolean>('showContents', optionScope)
  const [showMapPage] = useControlField<boolean>('showMapPage', optionScope)
  const [showBrokers] = useControlField<boolean>('showBrokers', optionScope)
  const [showImages] = useControlField<boolean>('showImages', optionScope)

  return (
    <Dialog modal={true} open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="flex h-full w-full max-w-none flex-col md:h-[calc(100vh-8rem)] md:w-[calc(100vw-8rem)]">
        <DialogHeader>
          <DialogTitle className="">Download PDF</DialogTitle>
        </DialogHeader>
        <div className="grid flex-1 grid-cols-12 gap-8 overflow-y-auto overflow-x-hidden bg-background">
          <FormProvider scope={form.scope()}>
            <form
              {...form.getFormProps()}
              className="col-span-12 flex flex-col gap-6 px-1 lg:col-span-4 lg:overflow-auto">
              <PdfTitle
                scope={rootScope.scope('title')}
                initialTitle={
                  listings.length === 1
                    ? listings[0].name || listings[0].property.address || ''
                    : ''
                }
              />
              <p className="col-span-12 -mb-4 text-lg font-medium">
                PDF Options
              </p>

              <PdfFormContent
                rootScope={rootScope}
                singleListing={singleListing}
                pdfOptions={{
                  type,
                  preparedFor,
                  notes,
                  showEcDevContact,
                  showTitlePage,
                  showContents,
                  showMapPage,
                  showBrokers,
                  showImages,
                }}
              />
            </form>
          </FormProvider>
          <div className="relative col-span-12 h-[500px] lg:col-span-8 lg:h-auto">
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-muted/50 pb-64">
              <Spinner className="h-10 w-10 text-primary" />
              <p className="mt-2">Loading...</p>
            </div>
            {isLoaded && !listingsQuery.isLoading && (
              <PdfClientBrowser
                document={
                  <ReportPdfSwitch
                    client={client}
                    title={title}
                    listings={sortedListings}
                    pdfOptions={{
                      type,
                      preparedFor,
                      notes,
                      showTitlePage,
                      showEcDevContact,
                      showContents,
                      showMapPage,
                      showBrokers,
                      showImages,
                    }}
                  />
                }
              />
            )}
          </div>
        </div>
        <DialogFooter className="flex justify-end">
          <DialogClose asChild>
            <Button variant="ghost">Close</Button>
          </DialogClose>

          <PdfClientDownload
            fileName={title}
            document={
              <ReportPdfSwitch
                client={client}
                title={title}
                listings={sortedListings}
                pdfOptions={{
                  type,
                  preparedFor,
                  notes,
                  showTitlePage,
                  showEcDevContact,
                  showContents,
                  showMapPage,
                  showBrokers,
                  showImages,
                }}
              />
            }>
            Download
          </PdfClientDownload>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
